/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { View, Modal, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import { StyleSheet } from 'react-native-unistyles';
import { TextView, IconView } from '@airasia-phoenix/react-native/components';
import {
  getTextStyle,
  pd,
  getColor,
} from '@airasia-phoenix/react-native/utils';
import { DialogRefs } from '@airasia-phoenix/react-native/components/DialogView/types';
import { useOptionalHotelsNavigation } from '../../context/HotelsNavigationContext';
import { CHINA_DISCLAIMER_COUNTDOWN } from '../../configs/constants';
import { ChinaDisclaimerModalProps } from '../../types';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';

export const ChinaDisclaimerModal = forwardRef<
  DialogRefs,
  ChinaDisclaimerModalProps
>(
  (
    { testID, imageViewProps, secondaryText, analyticsProps, redirectRoute },
    ref,
  ) => {
    const { t } = useTranslation('hotels');
    // Optional: hosts such as snap render this modal without the provider.
    const navigation = useOptionalHotelsNavigation();
    const { logEvent } = useLogger();
    const { EVENT_NAMES } = SearchWidgetMeta;

    const [isVisible, setIsVisible] = useState(false);
    const [countdown, setCountdown] = useState(CHINA_DISCLAIMER_COUNTDOWN);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const hasLoggedAnalyticsRef = useRef(false);

    const stopTimer = useCallback(() => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, []);

    const startTimer = useCallback(() => {
      stopTimer();
      setCountdown(CHINA_DISCLAIMER_COUNTDOWN);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }, [stopTimer]);

    // Handle Redirection
    useEffect(() => {
      if (isVisible && countdown === 0) {
        setIsVisible(false);
        stopTimer();

        if (navigation) {
          if (redirectRoute) {
            navigation.navigateToListUrl(redirectRoute);
          } else {
            navigation.navigateToList();
          }
          return;
        }

        // Legacy fallback when no provider is mounted (e.g. snap host).
        if (redirectRoute) {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.location.assign(redirectRoute);
          } else {
            const { router: nativeRouter } = require('expo-router');
            nativeRouter.push(redirectRoute);
          }
        }
      }
    }, [countdown, isVisible, navigation, stopTimer, redirectRoute]);

    // Track China disclaimer view event when modal opens
    useEffect(() => {
      if (isVisible && !hasLoggedAnalyticsRef.current) {
        const disclaimerMessage = t(secondaryText, {
          seconds: CHINA_DISCLAIMER_COUNTDOWN,
        });

        // Use custom analytics props if provided, otherwise fall back to hotel-specific analytics
        if (analyticsProps?.buildAnalyticsParams && analyticsProps?.eventName) {
          const analyticsParams =
            analyticsProps.buildAnalyticsParams(disclaimerMessage);
          logEvent(analyticsProps.eventName, analyticsParams);
        } else {
          // Default hotel analytics
          const analyticsParams = SearchWidgetEvent.chinaDisclaimerViewedEvent({
            disclaimerMessage,
          });
          logEvent(EVENT_NAMES.CHINA_DISCLAIMER_VIEWED, analyticsParams);
        }

        hasLoggedAnalyticsRef.current = true;
      }
    }, [
      isVisible,
      secondaryText,
      t,
      logEvent,
      analyticsProps,
      EVENT_NAMES.CHINA_DISCLAIMER_VIEWED,
    ]);

    // Cleanup on unmount
    useEffect(() => () => stopTimer(), [stopTimer]);

    useImperativeHandle(ref, () => ({
      openModal: () => {
        setIsVisible(true);
        startTimer();
      },
      closeModal: () => {
        setIsVisible(false);
        stopTimer();
        hasLoggedAnalyticsRef.current = false;
      },
      toggleModal: () => {
        if (isVisible) {
          setIsVisible(false);
          stopTimer();
          hasLoggedAnalyticsRef.current = false;
        } else {
          setIsVisible(true);
          startTimer();
        }
      },
    }));

    return (
      <Modal
        transparent
        visible={isVisible}
        animationType="fade"
        testID={testID}
        onRequestClose={() => {
          setIsVisible(false);
          stopTimer();
        }}
      >
        <View style={styles.backdrop}>
          <View style={styles.dialogContainer}>
            {imageViewProps && (
              <IconView
                url={imageViewProps.url}
                width={imageViewProps.width}
                height={imageViewProps.height}
              />
            )}

            <TextView
              testID="china-disclaimer-secondary-text"
              style={[
                getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_secondary,
                ),
                styles.textCenter,
              ]}
              text={t(secondaryText, { seconds: Math.max(countdown, 1) })}
            />
          </View>
        </View>
      </Modal>
    );
  },
);

ChinaDisclaimerModal.displayName = 'ChinaDisclaimerModal';

const styles = StyleSheet.create((pd) => ({
  backdrop: {
    flex: 1,
    backgroundColor: pd.sys.colour.overlay.toString(),
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogContainer: {
    width: { xs: '90%', md: 600 },
    minHeight: { xs: 300, md: 394 },
    backgroundColor: getColor(pd.sys.colour.container_none),
    borderRadius: 24,
    padding: pd.ref.space.spacing50.px,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  buttonRow: {
    width: '100%',
    alignItems: 'center',
  },
  image: {
    width: '100%',
    height: 150,
  },
  textCenter: {
    textAlign: 'center',
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400',
  },
}));

export default ChinaDisclaimerModal;
